import { Injectable } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { environment } from '@env';

export interface ISsoDescriptor {
  issuer: string;
  clientId: string;
  idpHint?: string;
}

const DISCOVERY_TIMEOUT_MS = 8000;

// ponytail: thin-slice Keycloak/OIDC PKCE service, configured at runtime from
// the subscriber's ssoDescriptor (not build-time environment.keycloak). Add
// token-refresh if needed.
@Injectable({ providedIn: 'root' })
export class KeycloakSsoService {

  private loginReady: Promise<boolean> = Promise.resolve(false);
  private configured = false;
  private discoveryFailedFlag = false;

  constructor(private oauthService: OAuthService) {
  }

  get isConfigured(): boolean {
    return this.configured;
  }

  get discoveryFailed(): boolean {
    return this.discoveryFailedFlag;
  }

  /** Configure the OAuth client from a runtime SSO descriptor (per-subscriber). */
  configure(descriptor: ISsoDescriptor): void {
    const config: AuthConfig = {
      issuer: descriptor.issuer,
      clientId: descriptor.clientId,
      // Redirect the OAuth code callback back to THIS route (the login page), not
      // the origin root. The app's base href is '/' and '/dev/...' is a route, so
      // a root redirect ('/') drops the ?code= when the router rewrites to the
      // default route. Returning to the current path lets angular-oauth2-oidc read
      // and exchange the code on reload.
      redirectUri: window.location.origin + window.location.pathname,
      // After single-logout, the IdP returns the browser here. Pin it to the login
      // route (not wherever logout was triggered). Must match the client's
      // "Valid post logout redirect URIs" registration on the IdP.
      postLogoutRedirectUri:
        window.location.origin + (environment.baseHref || '/').replace(/\/+$/, '') + '/pages/login',
      responseType: 'code',
      scope: 'openid profile email',
      // Keycloak's discovery doc doesn't always satisfy the strict endpoint check
      strictDiscoveryDocumentValidation: false,
      // PKCE is on by default in angular-oauth2-oidc v17
      customQueryParams: descriptor.idpHint ? { kc_idp_hint: descriptor.idpHint } : {},
    };
    this.oauthService.configure(config);
    this.configured = true;
    this.discoveryFailedFlag = false;
    this.loginReady = this.loadDiscoveryWithTimeout();
  }

  // Discovery hits the IdP's `.well-known/openid-configuration`, a third-party
  // endpoint that can be down/unreachable (IdP outage, CORS, air-gap misconfig).
  // Bound it so a stuck IdP never hangs app boot; treat timeout same as failure
  // (password login still loads, SSO button disabled with an error).
  private async loadDiscoveryWithTimeout(): Promise<boolean> {
    let timeoutHandle: ReturnType<typeof setTimeout>;
    try {
      return await Promise.race([
        this.oauthService.loadDiscoveryDocumentAndTryLogin(),
        new Promise<boolean>((_resolve, reject) => {
          timeoutHandle = setTimeout(() => reject(new Error('SSO discovery timed out')), DISCOVERY_TIMEOUT_MS);
        }),
      ]);
    } catch {
      this.discoveryFailedFlag = true;
      return false;
    } finally {
      clearTimeout(timeoutHandle);
    }
  }

  /**
   * Redirects to the configured IdP. No-op if not configured.
   *
   * The speculative discovery kicked off in configure() at page load can time out
   * against a cold/slow IdP, leaving discoveryFailedFlag set (the old "SSO
   * temporarily unavailable until you refresh" race). A click here is an explicit
   * intent to log in, so retry discovery on demand rather than staying failed —
   * this removes the need for a manual refresh. Only a click-time discovery
   * failure surfaces the error.
   */
  async login(): Promise<void> {
    if (!this.configured) { return; }
    // Discovery must be loaded before initCodeFlow can build the auth URL.
    await this.loginReady;
    if (this.discoveryFailedFlag) {
      this.discoveryFailedFlag = false;
      this.loginReady = this.loadDiscoveryWithTimeout();
      await this.loginReady;
      // Retry still failed: this is a real click-time failure, so reject rather
      // than silently no-op. The caller (LoginPage.keycloakSsoLogin) catches this
      // to disable the button + show the error — otherwise the failure would be
      // swallowed and the click would produce no feedback at all.
      if (this.discoveryFailedFlag) {
        throw new Error('SSO discovery failed');
      }
    }
    this.oauthService.initCodeFlow();
  }

  /**
   * Sign-out for the SSO path.
   * - `full` AND an SSO session is present (valid id_token): full single-logout —
   *   redirect to the IdP's end-session endpoint, which ends the IdP SSO
   *   session and returns to postLogoutRedirectUri (the login page). Returns true
   *   so the caller stops its own navigation (the browser is leaving the SPA).
   * - Otherwise (involuntary noauth, or no SSO token): just clear local OIDC
   *   state, no redirect — a deliberate re-login is then one click. Returns false.
   *
   * Only the explicit user "Sign Out" passes full=true; involuntary sign-outs
   * (handleNoAuth, guard denial, auth failure) must NOT nuke the IdP session.
   */
  logout(full: boolean): boolean {
    if (!this.configured) { return false; }
    if (full && this.oauthService.hasValidIdToken()) {
      this.oauthService.logOut();
      return true;
    }
    this.oauthService.logOut(true);
    return false;
  }

  /**
   * Resolves once discovery is loaded (or has failed/timed out) and any
   * redirect-back code has been exchanged. Await this before checking
   * hasIdToken(). Resolves false immediately if never configured.
   */
  tryLogin(): Promise<boolean> {
    return this.loginReady;
  }

  hasIdToken(): boolean {
    return this.configured && this.oauthService.hasValidIdToken();
  }

  getIdToken(): string {
    return this.oauthService.getIdToken();
  }

  getEmail(): string {
    const claims: any = this.oauthService.getIdentityClaims();
    return claims?.email ?? '';
  }
}
